import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { updateUserProfile, clearError } from "../../store/slices/userSlice";
import { withVersion } from "../../utils/concurrency";
import { UpdateProfileData, User as UserType } from "../../types";
import { updateProfileSchema } from "../../schemavalidations";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Calendar,
  Shield,
  Edit3,
  Save,
  X,
  MapPin,
  User as UserIcon,
  BarChart3,
} from "lucide-react";
import {
  clearError as clearCompanyError,
  getCompanyStats,
} from "../../store/slices/companySlice";
import { toast } from "react-toastify";
import Button from "../../components/UI/Button";
import { FormField, Input, Select } from "../../components/UI/FormField";
import { SectionCard } from "../../components/UI/SectionCard";
import { PasswordSection } from "../../components/profile/PasswordSection";
import { getMe } from "../../store/slices/authSlice";

const ProfilePage: React.FC = () => {
  const { t } = useTranslation("users");

  function getUserFromStorage(): UserType | null {
    const userStr = localStorage.getItem("user");
    if (!userStr) return null;
    try {
      return JSON.parse(userStr) as UserType;
    } catch {
      return null;
    }
  }

  const user = getUserFromStorage();
  const dispatch = useAppDispatch();

  const {
    companyStats,
    countries,
    error: companyError,
  } = useAppSelector((state) => state.company);

  const { error: userError } = useAppSelector((state) => state.users);

  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const isDriver = user?.role === "driver";

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    unregister,
    formState: { errors, isSubmitting },
  } = useForm<UpdateProfileData>({
    resolver: yupResolver(updateProfileSchema) as any,
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phone: user?.phone || "",
      address: {
        street: user?.address?.street || "",
        city: user?.address?.city || "",
        state: user?.address?.state || "",
        zipcode: user?.address?.zipcode || "",
        country: user?.address?.country || "",
      },
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const countryOptions = (countries ?? []).map((country) => ({
    value: country._id,
    label: country.name,
  }));

  useEffect(() => {
    if (!isDriver) {
      dispatch(getCompanyStats());
    }
  }, [dispatch]);

  useEffect(() => {
    if (companyError) {
      toast.error(companyError);
      dispatch(clearCompanyError());
    }
    if (userError) {
      toast.error(userError);
      dispatch(clearError());
    }
  }, [userError, dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      dispatch(getMe());
    }
  }, []);

  const handleEditToggle = () => {
    if (isEditing) {
      reset({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        phone: user?.phone || "",
        address: {
          street: user?.address?.street || "",
          city: user?.address?.city || "",
          state: user?.address?.state || "",
          zipcode: user?.address?.zipcode || "",
          country: user?.address?.country || "",
        },
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      setIsChangingPassword(false);
    }
    setIsEditing(!isEditing);
  };

  const onSubmit = async (data: UpdateProfileData) => {
    try {
      const submitData: Partial<UpdateProfileData> = {};

      if (data.firstName) submitData.firstName = data.firstName;
      if (data.lastName) submitData.lastName = data.lastName;
      if (data.phone !== undefined || data.phone !== "") {
        submitData.phone = data.phone;
      } else {
        submitData.phone = "";
      }

      if (data.address) {
        const hasAddressData = Object.values(data.address).some(
          (value) => value && value.trim() !== "",
        );
        if (hasAddressData) {
          submitData.address = data.address;
        }
      }

      if (isChangingPassword && data.newPassword && data.currentPassword) {
        submitData.currentPassword = data.currentPassword;
        submitData.newPassword = data.newPassword;
        submitData.confirmNewPassword = data.confirmNewPassword;
      }

      await dispatch(
        updateUserProfile({ userData: withVersion(submitData, user) }),
      ).unwrap();
      toast.success(t("profile.toast.updateSuccess"));
      setIsEditing(false);
      setIsChangingPassword(false);

      setValue("currentPassword", "");
      setValue("newPassword", "");
      setValue("confirmNewPassword", "");
    } catch (error) {}
  };

  const companyName =
    user?.companyId && typeof user.companyId === "object"
      ? user.companyId.name
      : t("profile.organization.defaultName");

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto mb-4"></div>
          <p>{t("profile.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 max-w-6xl mx-auto">
      <div className="mb-3">
        <h1 className="text-lg font-bold text-gray-900">
          {t("profile.page.title")}
        </h1>
        <p className="text-[12px] text-gray-500">
          {t("profile.page.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-2">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
            <SectionCard
              icon={UserIcon}
              title={t("profile.sections.personalInformation")}
              actions={
                !isEditing ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEditToggle}
                    icon={<Edit3 className="h-3.5 w-3.5" />}
                  >
                    {t("profile.buttons.edit")}
                  </Button>
                ) : null
              }
            >
              <div className="space-y-1">
                <FormField
                  layout="inline"
                  size="sm"
                  label={t("profile.fields.firstName")}
                  error={errors.firstName?.message}
                  required
                >
                  {isEditing ? (
                    <Input
                      {...register("firstName")}
                      size="sm"
                      error={!!errors.firstName}
                      placeholder={t("profile.placeholders.firstName")}
                    />
                  ) : (
                    <span className="text-[13px] text-gray-900 leading-8 inline-block">
                      {user.firstName}
                    </span>
                  )}
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label={t("profile.fields.lastName")}
                  error={errors.lastName?.message}
                  required
                >
                  {isEditing ? (
                    <Input
                      {...register("lastName")}
                      size="sm"
                      error={!!errors.lastName}
                      placeholder={t("profile.placeholders.lastName")}
                    />
                  ) : (
                    <span className="text-[13px] text-gray-900 leading-8 inline-block">
                      {user.lastName}
                    </span>
                  )}
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label={t("profile.fields.emailAddress")}
                >
                  <span className="text-[13px] text-gray-900 leading-8 inline-block">
                    {user.email}
                  </span>
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label={t("profile.fields.phone")}
                  error={errors.phone?.message}
                >
                  {isEditing ? (
                    <Input
                      {...register("phone")}
                      size="sm"
                      type="tel"
                      error={!!errors.phone}
                      placeholder={t("profile.placeholders.phone")}
                    />
                  ) : (
                    <span className="text-[13px] text-gray-900 leading-8 inline-block">
                      {user.phone || "-"}
                    </span>
                  )}
                </FormField>
              </div>
            </SectionCard>

            <SectionCard icon={MapPin} title={t("profile.sections.address")}>
              <div className="space-y-1">
                {isEditing ? (
                  <>
                    <FormField
                      layout="inline"
                      size="sm"
                      label={t("profile.fields.streetAddress")}
                      error={errors.address?.street?.message}
                    >
                      <Input
                        {...register("address.street")}
                        size="sm"
                        error={!!errors.address?.street}
                        placeholder={t("profile.placeholders.street")}
                      />
                    </FormField>

                    <FormField
                      layout="inline"
                      size="sm"
                      label={t("profile.fields.city")}
                      error={errors.address?.city?.message}
                    >
                      <Input
                        {...register("address.city")}
                        size="sm"
                        error={!!errors.address?.city}
                        placeholder={t("profile.placeholders.city")}
                      />
                    </FormField>

                    <FormField
                      layout="inline"
                      size="sm"
                      label={t("profile.fields.state")}
                      error={errors.address?.state?.message}
                    >
                      <Input
                        {...register("address.state")}
                        size="sm"
                        error={!!errors.address?.state}
                        placeholder={t("profile.placeholders.state")}
                      />
                    </FormField>

                    <FormField
                      layout="inline"
                      size="sm"
                      label={t("profile.fields.zipCode")}
                      error={errors.address?.zipcode?.message}
                    >
                      <Input
                        {...register("address.zipcode")}
                        size="sm"
                        error={!!errors.address?.zipcode}
                        placeholder={t("profile.placeholders.zipcode")}
                      />
                    </FormField>

                    <FormField
                      layout="inline"
                      size="sm"
                      label={t("profile.fields.country")}
                      error={errors.address?.country?.message}
                    >
                      <Select
                        {...register("address.country")}
                        size="sm"
                        error={!!errors.address?.country}
                        options={countryOptions}
                        placeholder={t("profile.placeholders.country")}
                      />
                    </FormField>
                  </>
                ) : (
                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("profile.fields.address")}
                  >
                    <span className="text-[13px] text-gray-900 leading-8 inline-block">
                      {[
                        user?.address?.street,
                        user?.address?.city,
                        user?.address?.state,
                        user?.address?.zipcode,
                        countryOptions.find(
                          (c) => c.value === user.address?.country,
                        )?.label,
                      ]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </span>
                  </FormField>
                )}
              </div>
            </SectionCard>

            {isEditing && (
              <PasswordSection
                register={register}
                unregister={unregister}
                setValue={setValue}
                errors={errors}
                watch={watch}
                isChangingPassword={isChangingPassword}
                setIsChangingPassword={setIsChangingPassword}
              />
            )}

            {isEditing && (
              <div className="flex justify-end items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-md">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleEditToggle}
                  icon={<X className="h-3.5 w-3.5" />}
                >
                  {t("profile.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isSubmitting}
                  icon={<Save className="h-3.5 w-3.5" />}
                >
                  {isSubmitting
                    ? t("profile.buttons.saving")
                    : t("profile.buttons.saveChanges")}
                </Button>
              </div>
            )}
          </form>
        </div>

        {/* Organization Info Sidebar */}
        <div className="space-y-2">
          <SectionCard
            icon={Building2}
            title={t("profile.sections.organization")}
          >
            <dl className="space-y-1.5 text-[13px]">
              <div className="flex gap-2">
                <dt className="w-24 text-gray-500 text-[12px]">
                  {t("profile.organization.label")}
                </dt>
                <dd className="text-gray-900 font-medium">{companyName}</dd>
              </div>
              <div className="flex gap-2 items-center">
                <dt className="w-24 text-gray-500 text-[12px] flex items-center gap-1">
                  <Shield className="h-3.5 w-3.5 text-gray-400" />
                  {t("profile.organization.roleLabel")}
                </dt>
                <dd className="text-gray-900 capitalize">{user.role}</dd>
              </div>
              <div className="flex gap-2 items-center">
                <dt className="w-24 text-gray-500 text-[12px] flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" />
                  {t("profile.organization.memberSince")}
                </dt>
                <dd className="text-gray-900">
                  {new Date(user.createdAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </SectionCard>

          {!isDriver && (
            <SectionCard
              icon={BarChart3}
              title={t("profile.sections.quickStats")}
            >
              <dl className="space-y-1 text-[13px]">
                <div className="flex justify-between">
                  <dt className="text-gray-500">
                    {t("profile.stats.teamMembers")}
                  </dt>
                  <dd className="font-medium text-gray-900">
                    {companyStats?.totalUsers || 0}
                  </dd>
                </div>
                {user?.role === "admin" && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">
                      {t("profile.stats.pendingInvites")}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {companyStats?.pendingInvites || 0}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t("profile.stats.admin")}</dt>
                  <dd className="font-medium text-gray-900">
                    {companyStats?.adminUsers || 0}
                  </dd>
                </div>
              </dl>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
